import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Loader2, CheckCircle, Building2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PORTAL_NAMES = {
  hoa_admin: 'HOA Administration Portal',
  hoa_owner: 'Homeowner Portal',
  landlord: 'Landlord Portal',
  property_manager: 'Property Manager Portal',
  vendor: 'Vendor Portal',
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [memberships, setMemberships] = useState([]);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      verifyToken(tokenParam);
    } else {
      toast.error('Invalid reset link');
      setTimeout(() => navigate('/forgot-password'), 2000);
    }
  }, [searchParams]);

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await axios.post(`${API}/auth/verify-reset-token`, { token: tokenToVerify });
      if (response.data.valid) {
        setTokenValid(true);
      } else {
        toast.error('Invalid or expired reset link');
        setTimeout(() => navigate('/forgot-password'), 2000);
      }
    } catch (error) {
      toast.error('Invalid or expired reset link');
      setTimeout(() => navigate('/forgot-password'), 2000);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      if (response.data.success) {
        setResetSuccess(true);
        setMemberships(response.data.memberships || []);
        toast.success('Password reset successfully!');
      }
    } catch (error) {
      toast.error('Password reset failed', { description: error.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-emerald-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600 mb-4" />
            <p className="text-gray-600">Verifying reset link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-emerald-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {resetSuccess ? <CheckCircle className="h-12 w-12 text-green-600" /> : <Lock className="h-12 w-12 text-emerald-600" />}
          </div>
          <CardTitle className="text-2xl">
            {resetSuccess ? 'Password Updated Successfully' : 'Set New Password'}
          </CardTitle>
          <CardDescription>
            {resetSuccess
              ? 'Your password has been updated for your APC EstateFlow account.'
              : 'Choose a strong password for your account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!resetSuccess ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input id="newPassword" type="password" placeholder="Enter new password" value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)} className="pl-10" required minLength={8} />
                </div>
                <p className="text-xs text-gray-500">Must be at least 8 characters</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input id="confirmPassword" type="password" placeholder="Confirm new password" value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)} className="pl-10" required />
                </div>
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading}>
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Resetting Password...</> : 'Reset Password'}
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              {memberships.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Your Available Access</p>
                  <div className="space-y-2">
                    {memberships.map((m, i) => (
                      <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2.5 border border-gray-100">
                        <div>
                          <p className="font-medium text-sm text-gray-900">{m.org_name}</p>
                          <p className="text-xs text-gray-500">{m.role_display}</p>
                        </div>
                        <span className="text-xs text-gray-400">{m.portal_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate('/')}>
                Continue to APC EstateFlow
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
